import { useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { Account } from "@/api/rest/models/account";
import { HouseholdData } from "@/api/rest/models/household";
import { CaseType } from "@/api/rest/models/warranty";
import { forceSubmitCase } from "@/api/rest/services/warrantyService.ts";
import { sendToSentry } from "@/utils/sentry";

import SubmissionForm from "./SubmissionForm";
import { useSubmissionForm } from "./useSubmissionForm";

interface DirectSubmissionFormProps {
  householdData: HouseholdData;
  availableAccounts: Account[];
  refreshCases: () => void;
}

const DirectSubmissionForm: React.FC<DirectSubmissionFormProps> = ({ householdData, availableAccounts }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const caseRef = useRef<HTMLTextAreaElement>(null);
  const titleRef = useRef<HTMLInputElement>(null);

  const {
    uploadedDraftFiles,
    isSubmittingCase,
    setSubmittingState,
    isFileUploaded,
    submitFiles,
    pendingFiles,
    uploadPendingFiles,
    uploadProgress,
    submitError,
    setSubmitError,
    clientSubmissionId,
  } = useSubmissionForm({
    householdData,
  });

  const handleSubmit = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      const value = caseRef?.current?.value.trim() ?? "";
      const selectedAccount = availableAccounts?.[0]?.id;
      if (!value || !selectedAccount) {
        return;
      }
      setSubmittingState(true);
      const uploads = await uploadPendingFiles();
      if (!uploads) {
        setSubmittingState(false);
        return;
      }
      const result = await forceSubmitCase(
        {
          description: value,
          householdId: householdData.household.id,
          title: titleRef.current?.value ?? "Unknown",
          accountId: selectedAccount,
          phoneNumber: "",
          type: CaseType.WARRANTY,
          clientSubmissionId,
        },
        uploads,
        [], // Pass empty history for direct submission
      );
      if (result.success) {
        navigate(`/warranty/${result.result.id}`);
      } else {
        sendToSentry(new Error(result.message), {
          additionalContext: {
            component: "DirectSubmissionForm.submitForm",
            result,
          },
        });
        setSubmitError(result.message);
        setSubmittingState(false);
      }
    },
    [
      availableAccounts,
      setSubmittingState,
      householdData.household.id,
      uploadPendingFiles,
      setSubmitError,
      navigate,
      clientSubmissionId,
    ],
  );

  return (
    <>
      <span className="flex items-center">
        <h3 className="text-xl font-semibold inline">{t("warranty.forceSubmitCase")}</h3>
      </span>
      <SubmissionForm
        handleSubmit={handleSubmit}
        pendingFiles={pendingFiles}
        uploadedDraftFiles={uploadedDraftFiles}
        onPhoneChange={null}
        isFileUploadMandatory={false} // always false for DirectSubmissionForm
        isFileUploaded={isFileUploaded}
        isSubmittingCase={isSubmittingCase}
        submitFiles={submitFiles}
        uploadProgress={uploadProgress}
        submitError={submitError}
      >
        <input
          className="border border-gray-300 rounded-md p-2 w-full"
          name="title"
          placeholder={t("warranty.caseTitle")}
          ref={titleRef}
          required
        />
        <textarea
          className="border border-gray-300 rounded-md p-2 w-full"
          name="report"
          rows={5}
          placeholder={t("warranty.caseDescription")}
          ref={caseRef}
          required
        />
      </SubmissionForm>
    </>
  );
};

export default DirectSubmissionForm;
